/**
 * OrgAdvisorDashboard - Slate home (production list).
 * Empty slate shows a large “Add your first project” CTA.
 * Layout matched to https://global-mobility-line-producer.yentien.chatgpt.site/
 */

import React, { useCallback, useEffect, useState } from 'react';
import { getLoginPath, getOrgUploadPath } from '../utils/postLoginNavigation';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LpWorkbenchShell from '../components/org/LpWorkbenchShell';
import { orgAuthApi } from '../services/api';
import { countryNameFromIso2 } from '../utils/countries';
import '../styles/LpWorkbench.css';

function slateLocationLine(production) {
  const destCode = String(production?.destination_country || '').trim().toUpperCase();
  if (destCode) return countryNameFromIso2(destCode) || destCode;
  return String(production?.primary_location || '').trim();
}

export default function OrgAdvisorDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [productions, setProductions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const loadProductions = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const res = await orgAuthApi.listProjects();
      setProductions(res.data?.projects || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate(getLoginPath(), { replace: true });
        return;
      }
      setLoadError(t('lpWorkbench.loadProductionsError'));
    } finally {
      setLoading(false);
    }
  }, [navigate, t]);

  useEffect(() => { loadProductions(); }, [loadProductions]);

  function addProduction() {
    navigate(getOrgUploadPath());
  }

  if (loading) {
    return (
      <LpWorkbenchShell bare slateTheme portfolioChrome hideLanguage>
        <div className="lp-slate-stage">
          <div className="lp-slate-screen">
            <div className="lp-slate-take">{t('lpWorkbench.loading')}</div>
            <div className="lp-slate-h1">{t('lpWorkbench.loadingProductions')}</div>
          </div>
        </div>
      </LpWorkbenchShell>
    );
  }

  if (!loadError && productions.length === 0) {
    return (
      <LpWorkbenchShell bare slateTheme portfolioChrome hideLanguage>
        <div className="lp-slate-stage">
          <button
            type="button"
            className="lp-slate-screen lp-slate-empty-cta"
            onClick={addProduction}
          >
            <div className="lp-slate-h1">{t('lpWorkbench.addFirstProject')}</div>
            <div className="lp-slate-empty-hint">{t('lpWorkbench.addFirstProjectHint')}</div>
          </button>
        </div>
      </LpWorkbenchShell>
    );
  }

  return (
    <LpWorkbenchShell slateTheme portfolioChrome hideLanguage>
      <div className="lp-slate-list">
        <div className="lp-slate-list-h slate-heading">
          <div>
            <h1>{t('lpWorkbench.productions')}</h1>
          </div>
          <button type="button" className="lp-slate-new-btn" onClick={addProduction}>
            {t('lpWorkbench.newProductionButton')}
          </button>
        </div>

        {loadError ? (
          <div className="lp-banner" style={{ background: '#FBF1EF', borderColor: '#E8C4BE' }}>
            <div className="lp-bn-main">
              <div className="lp-bn-txt"><b>{loadError}</b></div>
            </div>
            <button type="button" className="lp-bn-btn" onClick={loadProductions}>{t('lpWorkbench.retry')}</button>
          </div>
        ) : null}

        {productions.length ? (
          <div className="lp-slate-cards">
            {productions.map((production) => {
              const country = slateLocationLine(production) || '—';
              const participants = Number(production.participant_count) || 0;
              return (
                <button
                  key={production.id}
                  type="button"
                  className="lp-slate-card"
                  onClick={() => navigate(`/org/productions/${production.id}`)}
                >
                  <span className="lp-slate-card-name">
                    <strong>{production.name}</strong>
                  </span>
                  <span className="lp-slate-stat">
                    <small>{t('lpWorkbench.country')}</small>
                    <strong>{country}</strong>
                  </span>
                  <span className="lp-slate-stat">
                    <small>{t('lpWorkbench.participants')}</small>
                    <strong>{participants}</strong>
                  </span>
                  <span className="lp-slate-card-chevron" aria-hidden="true">›</span>
                </button>
              );
            })}
          </div>
        ) : null}
      </div>
    </LpWorkbenchShell>
  );
}
